var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import Store from '@data/stores/Store';
import { PaymentSettingsService } from '@data/api';
import { getExternalPaymentBaseUrl } from '@config/index';
import { FeatureFlag, FeatureFlagManager } from '@business/feature-flags';
import { createReservationRequest } from '@business/utils/reservation';
import { getClickIdForBooking } from '@data/stores/Amplify/getClickIdForBooking';
import { system } from '@config/system';
const getPaymentEnv = () => {
    var _a;
    if (system.environment === 'devbox') {
        return 'dev';
    }
    return ((_a = system.environment) !== null && _a !== void 0 ? _a : 'prod');
};
export const getExternalPaymentUrl = (language, propertyCode) => {
    return `${getExternalPaymentBaseUrl()}/${language}/reservation/${propertyCode}/payment-processor`;
};
const getMainGuestData = () => {
    const mainGuest = Store.property.guests.main;
    return Object.assign(Object.assign({}, mainGuest), { billingDetails: {
            billingAddress1: mainGuest.address1 || '',
            billingAddress2: mainGuest.address2 || '',
            billingCity: mainGuest.city || '',
            billingCountry: mainGuest.country || '',
            billingDocumentNumber: mainGuest.taxIdNumber || '',
            billingDocumentType: '', // required for payment SDK but not used in our flow
            billingEmail: mainGuest.email,
            billingState: mainGuest.state || '',
            billingZip: mainGuest.zipCode || '',
        } });
};
// Exposing this function to be used in the tests
export const getCustomPaymentOption = () => {
    return Store.property.customPaymentOption;
};
const isUsingSmartPolicies = () => {
    var _a, _b;
    const isPropertyUseSmartPolicies = !!((_b = (_a = Store.property.model) === null || _a === void 0 ? void 0 : _a.settings) === null || _b === void 0 ? void 0 : _b.smartPoliciesEnabled);
    const isSmartPolicyEnabledFF = FeatureFlagManager.isEnabled(FeatureFlag.enableSmartPolicies);
    return isSmartPolicyEnabledFF && isPropertyUseSmartPolicies;
};
export const isCfarInCart = () => !!Store.property.cart.travelInsurance &&
    Store.property.cart.travelInsurance.offerId !== 'none';
// Conditions to send "offline_only":
// - The user has added CFAR to the cart, or
// - The cart contains a smart policy rate plan AND has a payment schedule
export const getPaymentMethodFilters = () => {
    var _a;
    const isAccommodationsWithSmartPolicyInCart = isUsingSmartPolicies() &&
        Store.property.cart.accommodations.some((accommodation) => !!accommodation.item.smartPolicyRootId);
    // Must have at least one that is not the initial deposit
    const isPaymentScheduled = (_a = Store.property.cart.totals) === null || _a === void 0 ? void 0 : _a.paymentSchedule.some((item) => !item.isInitialDeposit);
    const offlineOnly = isCfarInCart() ||
        (isAccommodationsWithSmartPolicyInCart && isPaymentScheduled);
    return { paymentMethodFilters: offlineOnly ? ['offline_only'] : undefined };
};
export const getBasePaymentProps = () => {
    var _a, _b;
    const propertyModel = Store.property.model;
    const apiKey = (propertyModel === null || propertyModel === void 0 ? void 0 : propertyModel.paymentAPIKey) || '';
    const { currency, language } = Store.environment;
    const gateway = ((_a = propertyModel === null || propertyModel === void 0 ? void 0 : propertyModel.paymentGateway) === null || _a === void 0 ? void 0 : _a.name) || '';
    const customPaymentOption = getCustomPaymentOption();
    const priceDeposit = ((_b = Store.property.cart.totals) === null || _b === void 0 ? void 0 : _b.deposit) || 0;
    const propertyId = (propertyModel === null || propertyModel === void 0 ? void 0 : propertyModel.id) || 0;
    const paymentMethodFilters = getPaymentMethodFilters();
    return Object.assign(Object.assign({ apiKey,
        currency,
        customPaymentOption, env: getPaymentEnv(), gateway, guest: getMainGuestData(), isCfar: isCfarInCart(), lang: language }, paymentMethodFilters), { priceDeposit,
        propertyId });
};
export const getExternalPaymentSystemProps = (appParams, windowMode) => {
    var _a;
    const basePaymentProps = getBasePaymentProps();
    const { aiSource, googleHotelAds, origin, referralId } = appParams;
    const propertyCode = (_a = Store.property.model) === null || _a === void 0 ? void 0 : _a.code;
    const clickId = propertyCode ? getClickIdForBooking(propertyCode) : null;
    const reservationRequest = createReservationRequest({
        aiSource,
        googleHotelAds,
        origin,
        payment: Object.assign({}, (clickId && { clickId })),
        referralId,
        termsAccepted: false,
    });
    return Object.assign(Object.assign({}, basePaymentProps), { 
        // The card is tokenized in the popup and /prepare is always delegated to the
        // main window, which owns reservation-request deduplication.
        handlePrepareOnMainWindow: true, reservationRequest,
        windowMode });
};
const doesPaymentScheduleHaveDeposit = () => {
    const cartTotals = Store.property.cart.totals;
    return ((cartTotals === null || cartTotals === void 0 ? void 0 : cartTotals.expectedPaymentSchedule) || []).some((paymentScheduleItem) => paymentScheduleItem.isInitialDeposit && paymentScheduleItem.amount > 0);
};
export const isPayLater = () => {
    var _a;
    if (isUsingSmartPolicies()) {
        return !doesPaymentScheduleHaveDeposit();
    }
    const propertyModel = Store.property.model;
    const propertyCollectsNoDeposit = ((_a = propertyModel === null || propertyModel === void 0 ? void 0 : propertyModel.deposit) === null || _a === void 0 ? void 0 : _a.depositType) === 'no_deposit';
    return propertyCollectsNoDeposit;
};
export const isCreditCardCollectRequired = () => {
    var _a, _b;
    if (isUsingSmartPolicies()) {
        const cartTotals = Store.property.cart.totals;
        return (_a = cartTotals === null || cartTotals === void 0 ? void 0 : cartTotals.smartPolicyCaptureCreditCard) !== null && _a !== void 0 ? _a : true;
    }
    if (!((_b = Store.property.model) === null || _b === void 0 ? void 0 : _b.deposit)) {
        return false;
    }
    const { depositType, noDepositCCCollect } = Store.property.model.deposit;
    return depositType !== 'no_deposit' || noDepositCCCollect;
};
// Skips the payment step in case of Pay Later without credit card collect required
export const isSkipPayment = () => {
    return isPayLater() && !isCreditCardCollectRequired();
};
// Methods that don't produce a tokenized instrument (today just Bank Transfer);
// a set so future ones are covered automatically.
const NON_TOKENIZABLE_METHODS = new Set(['ebanking']);
// True when every eligible method is non-tokenizable — including the empty case
// (nothing to tokenize, so the popup adds no value).
export const isNonTokenizableOnly = (eligibleMethods) => {
    return eligibleMethods.every((method) => NON_TOKENIZABLE_METHODS.has(method));
};
// Live eligible methods for the current property + cart. Source of truth for the
// popup-vs-inline decision; [] when there is no property data to query.
export const fetchEligiblePaymentMethods = () => __awaiter(void 0, void 0, void 0, function* () {
    const propertyModel = Store.property.model;
    const apiKey = (propertyModel === null || propertyModel === void 0 ? void 0 : propertyModel.paymentAPIKey) || '';
    const propertyId = (propertyModel === null || propertyModel === void 0 ? void 0 : propertyModel.id) || 0;
    if (!apiKey || !propertyId) {
        return [];
    }
    const { paymentMethodFilters } = getPaymentMethodFilters();
    const settings = yield PaymentSettingsService.fetchSettings({
        apiKey,
        isCfar: isCfarInCart(),
        paymentMethodFilters,
        propertyId,
    });
    return settings.eligibleMethods;
});
